import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProperties } from '../../store/actions/properties';
import {
  fetchPropertySuites,
  clearSuites,
  fetchPropertieSuites,
} from '../../store/actions/suites';
import {
  setContactFormData,
  clearContactFormData,
} from '../../store/actions/contactForm';
import { saveContact } from '../../store/actions/contacts';
import { GOOGLE_API_KEY, USER_ROLES } from '../../common/constants';
import { selectProperties } from '../../store/selectors/properties';
import { selectSuites } from '../../store/selectors/suites';
import { selectContactFormData } from '../../store/selectors/contactForm';
import { fetchAllIssueTypes } from '../../store/actions/formOptions';
import Button from '../Button/Button';
import Form from '../Form';
import { selectContacts } from '../../store/selectors/contacts';
import { selectAuthenticatedUser } from '../../store/selectors/auth';
import Loader from '../Loader';
import { selectFormLoader } from '../../store/selectors/FormLoader';
import { selectValidation } from '../../store/selectors/Validation';
import { clearValidation } from '../../store/actions/validation';
import { stopFormLoading } from '../../store/actions/loader';
import * as client from '../../services/client';
import { toast } from 'react-toastify';
import { variables } from '../../Tenants';
import ContactFormContent from './ContactFormContent';
import 'react-toastify/dist/ReactToastify.css';
import { getServiceProviderProperties } from '../../services/client';

// Regular expression for validating an email format.
const validEmailRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

/* This is a complex form component for creating and editing contacts in the system.
 * It handles multiple user roles with different field requirements for each role. Data flow:
 * 1. Redux store -> props -> component state/UI.
 * 2. User input -> local state + redux -> validation -> submission.
 * 3. Submission response -> update UI or close form. */
const ContactForm = ({
  onClose,
  onSubmit,
  sendResetPasswordLinkToUser,
  sendActivationLinkToUser,
  isFromPropertyDeveloper,
}) => {
  // Redux state using hooks.
  const dispatch = useDispatch();
  const contactFormData = useSelector(selectContactFormData);
  const properties = useSelector(selectProperties);
  const propertySuites = useSelector(selectSuites);
  const contacts = useSelector(selectContacts);
  const user = useSelector(selectAuthenticatedUser);
  const types = useSelector((state) => state.formOptions.types);
  const loader = useSelector(selectFormLoader);
  const validation = useSelector(selectValidation);

  // State using a single object like in the original class component
  const [state, setState] = useState({
    buttonPressed: '', // Tracks which button was clicked for form submission.
    errors: {}, // Stores validation error messages.
    location: '', // Current address search input.
    predictions: [], // Google address search results.
    suitesOptions: [], // suitesOptions: Available suites options.
    suitesList: [], // suitesList: Selected suites for the contact.
    propertiesList: [], // propertiesList: Selected properties for the contact.
  });

  // Action dispatcher functions to the Redux Store.
  const onFetchProperties = (userId, role) =>
    dispatch(fetchProperties(userId, role));
  const onFetchIssueTypes = () => dispatch(fetchAllIssueTypes());
  const onStopFormLoading = () => dispatch(stopFormLoading());
  const onSetContactFormData = (data) => dispatch(setContactFormData(data));
  const doClearContactFormData = () => dispatch(clearContactFormData());
  const doClearSuites = () => dispatch(clearSuites());
  const doClearValidation = () => dispatch(clearValidation());
  const onFetchPropertySuites = (propertyId, userId, role) =>
    dispatch(fetchPropertySuites(propertyId, userId, role));
  const onFetchPropertiesSuites = (params) =>
    dispatch(fetchPropertieSuites(params));

  // This will detect when the selected user type changes, it'll clear out the propertiesList from state.
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      suitesList: [],
      propertiesList: [],
    }));
  }, [contactFormData.userRole]);

  const verifyProperties = async () => {
    try {
      const response = await getServiceProviderProperties(contactFormData.id);

      if (response.ok) {
        const data = await response.json();

        setState((prevState) => ({
          ...prevState,
          suitesList: [],
          propertiesList: data.data,
        }));
      } else {
        console.error(
          'Failed to fetch service provider properties:',
          response.status
        );
      }
    } catch (error) {
      console.error('Error checking service provider properties:', error);
    }
  };

  // Initialize component data and fetch necessary information on mount
  useEffect(() => {
    verifyProperties();
    onStopFormLoading();
    onFetchIssueTypes();
    onFetchProperties(user.id, user.roles[0].role.name);

    // For new contacts: clear suites and set default role.
    if (!contactFormData.id) {
      doClearSuites();
      return onSetContactFormData({ userRole: 'default' });
    }

    // Extract user role from contact data.
    const userRole = contactFormData?.roles?.[0]?.role?.name;

    // Handle contacts without a role.
    if (!userRole) {
      const defaultRole = isFromPropertyDeveloper
        ? 'Property Developer'
        : 'default';
      return onSetContactFormData({ userRole: defaultRole });
    }

    // Set up Occupant role.
    if (userRole === USER_ROLES.OCCUPANT) {
      // Fetch property suites from Redux.
      onFetchPropertySuites(
        contactFormData.propertyId,
        user.id,
        user.roles[0].role.name
      );

      // Map existing suite data for immediate use.
      const Suites =
        contactFormData.userSuites.length > 0
          ? contactFormData.userSuites.map((a) => ({
              Id: a.suiteId,
              name: a.suiteName,
              PropertyId: contactFormData.propertyId,
            }))
          : [];

      onSetContactFormData({ Suites, userRole });
      return;
    }

    // Set up Home Owner and Realtor roles.
    if (userRole === USER_ROLES.HOME_OWNER || userRole === USER_ROLES.REALTOR) {
      // Map property data.
      let properties =
        contactFormData.properties?.length > 0
          ? contactFormData.properties.map((a) => ({
              Id: a.propertyId,
              name: a.name,
            }))
          : [];

      if (properties.length === 0) return;

      // Remove duplicate properties.
      properties = [
        ...new Map(properties.map((item) => [item.Id, item])).values(),
      ];

      // Prepare API request parameters.
      const params = {
        Properties: properties.map((x) => x.Id),
        USER_TYPESserId: user.id,
        Role: user.roles[0].role.name,
      };

      // Fetch suite data for these properties from Redux.
      onFetchPropertiesSuites(params);

      // Map existing suite data
      const Suites =
        contactFormData.userSuites.length > 0
          ? contactFormData.userSuites.map((a) => ({
              Id: a.suiteId,
              name: a.propertyName + ' - ' + a.suiteName,
              propertyId: a.propertyId,
            }))
          : [];

      // Update Redux and local state with the updated properties and suites.
      onSetContactFormData({ Suites, userRole });

      // Set state for the property list and suites for Realtors and Homeowners from the API.
      return setState((prevState) => ({
        ...prevState,
        propertiesList: properties,
        suitesList: Suites,
      }));
    }

    // Set up Service Manager role.
    if (userRole === USER_ROLES.SERVICE_MANAGER) {
      onSetContactFormData({ userRole });
      return onFetchPropertySuites(
        contactFormData.propertyId,
        user.id,
        user.roles[0].role.name
      );
    }

    // Set up Service Provider role.
    if (userRole === USER_ROLES.SERVICE_PROVIDER) {
      return onSetContactFormData({
        userRole,
        serviceType: contactFormData.serviceProvided,
      });
    }

    // Default case: just set the role.
    onSetContactFormData({ userRole });
  }, []);

  // useEffect to replace 'componentDidUpdate'. Handles form submission responses.
  useEffect(() => {
    if (validation.status === true) {
      if (
        validation.message === 'Contact' &&
        state.buttonPressed === 'saveContact'
      ) {
        onClose();
        doClearContactFormData();
        doClearValidation();
      } else if (
        validation.message === 'Contact' &&
        state.buttonPressed === 'saveAndAdd'
      ) {
        doClearContactFormData();
        doClearValidation();
      }
    }
  }, [validation, state.buttonPressed, onClose]);

  // Validates all form fields based on the selected user role. Todo: Refactor logic in here to be less messy.
  const validateContactFormData = (actionType) => {
    // Track which button was pressed for handling the response later.
    setState((prevState) => ({
      ...prevState,
      buttonPressed: actionType,
    }));

    // Regex for validating name fields.
    let name = /^[a-zA-Z0-9\u00C0-\u017F-',. &*]*$/;
    let errors = {};

    if (contactFormData.firstname.trim().length === 0) {
      errors.firstname = 'Please enter  the first name !';
      errors.errors = true;
    } else if (!contactFormData.firstname.match(name)) {
      errors.firstname =
        "Please use only letters, numbers, spaces, and the following characters: , . & *'";
      errors.errors = true;
    }

    if (contactFormData.lastname.trim().length === 0) {
      errors.lastname = 'Please enter the last name !';
      errors.errors = true;
    }

    if (contactFormData.email.trim().length === 0) {
      errors.email = 'Please enter the  email address !';
      errors.errors = true;
    }

    if (!validEmailRegex.test(contactFormData.email.toLowerCase())) {
      errors.email = 'Please enter the email address !';
      errors.errors = true;
    }

    if (contactFormData.phone.length < 10) {
      errors.phone = 'Please Enter a 10 digit Phone Number';
      errors.errors = true;
    }

    if (contactFormData.phone.length > 20) {
      errors.phone = 'Phone Number can not be more than 20 digits';
      errors.errors = true;
    }

    if (contactFormData.userRole === 'default') {
      errors.userRole = 'Please Select A Role';
      errors.errors = true;

      if (contactFormData.roles) {
        delete errors.type;
        errors.errors = false;
      }
    }

    if (contactFormData.userRole === 'Home Owner') {
      if (
        contactFormData.properties.length == 0 ||
        contactFormData.Suites.length == 0
      ) {
        errors.type = `Please Select A Property and ${variables.Suite} for Home Owner`;
        errors.errors = true;
      }
    }

    if (contactFormData.userRole === 'Realtor') {
      if (
        contactFormData.properties.length == 0 ||
        contactFormData.Suites.length == 0
      ) {
        errors.type = `Please Select A Property and ${variables.Suite} for Realtor`;
        errors.errors = true;
      }
    }

    if (contactFormData.userRole === 'Occupant') {
      if (!contactFormData.propertyId || !contactFormData.suiteId) {
        errors.type = `Please Select A Property and ${variables.Suite} for Occupant`;
        errors.errors = true;
      }
    }

    // I am working here.
    if (contactFormData.userRole === USER_ROLES.SERVICE_PROVIDER) {
      if (!contactFormData.servicesProvided) {
        errors.userRole = 'Please Select Service Provided by Service Provider';
        errors.errors = true;
      }

      if (!contactFormData.companyName) {
        errors.userRole =
          'You must include a Company Name for a new service provider';
        errors.errors = true; // This is so gross lmao, but it will do for now.
      }
    }

    // Check for duplicate contacts when creating a new contact.
    if (!contactFormData.id) {
      for (let i = 0; i < contacts.length; i++) {
        if (contacts[i].email === contactFormData.email.trim()) {
          errors.email = 'This contact already exists.';
          errors.errors = true;
          toast.error(' 🚫 This contact already exists !', {
            autoClose: 10000,
            position: 'top-center',
          });
        }
      }
    }

    setState((prevState) => ({
      ...prevState,
      errors: errors,
    }));

    if (errors.errors) {
      console.error(errors.errors);
    } else {
      // Todo: This is a hack and root cause needs to be fixed in: https://app.asana.com/1/41213529744312/project/1208712340140532/task/1209942667296578?focus=true
      if (contactFormData.userRole === 'Service Provider') {
        // Extract property IDs from local state
        const propertyIds = state.propertiesList.map((property, index) => {
          const propertyId = property.Id || property.id;
          return propertyId;
        });

        // Create an updated version of contactFormData with the current property IDs
        const updatedData = {
          ...contactFormData,
          ServiceProviderAssignedProperties: propertyIds,
        };

        // Submit the updated data instead of the Redux state
        onSubmit(updatedData, actionType);
      } else {
        onSubmit(contactFormData, actionType);
      }
    }
  };

  // Determines if the submit button should be enabled. Todo: Refactor logic in here to be less messy.
  const enableButton = () => {
    const {
      firstname,
      lastname,
      email,
      phone,
      userRole,
      propertyId,
      suiteId,
      servicesProvided,
      companyName,
    } = contactFormData;

    // Initialize variable to track if the button should be disabled or not.
    let disabled = true,
      _properties = '',
      _suites = '';

    if (state.propertiesList.length === 0) {
      _properties = [];
    } else {
      _properties = state.propertiesList;
    }

    if (state.suitesList.length === 0) {
      _suites = [];
    } else {
      _suites = state.suitesList;
    }

    /* Todo - Refactor this, it's a bit of an eyesore.
     * This block validates that suites belong to selected properties. */
    if (
      (userRole === 'Home Owner' || userRole === 'Realtor') &&
      !!_suites &&
      _suites.length > 0 &&
      _properties.length > 0
    ) {
      for (var i = 0; i < _properties.length; i++) {
        let filterSuite = _suites.filter(
          (x) => x.propertyId == _properties[i].Id
        );

        if (filterSuite.length == 0) {
          _suites = [];
        }
      }
    }

    if (userRole === 'Home Owner') {
      disabled =
        !firstname ||
        !lastname ||
        !email ||
        !phone ||
        !userRole ||
        !_properties.length > 0 ||
        !_suites.length > 0;
    }

    if (userRole === 'Realtor') {
      disabled =
        !firstname ||
        !lastname ||
        !email ||
        !phone ||
        !userRole ||
        !_properties.length > 0 ||
        !_suites.length > 0;
    }

    if (userRole === 'Occupant') {
      disabled =
        !firstname ||
        !lastname ||
        !email ||
        !phone ||
        !userRole ||
        !propertyId ||
        !suiteId;
    }

    if (userRole === 'Vendor') {
      disabled =
        !firstname ||
        !lastname ||
        !email ||
        !phone ||
        !userRole ||
        (servicesProvided && !servicesProvided.length > 0);
    }

    if (userRole === 'Service Provider') {
      disabled =
        !firstname ||
        !lastname ||
        !email ||
        !phone ||
        !userRole ||
        !companyName ||
        (servicesProvided && !servicesProvided.length > 0);
    }

    if (userRole === 'Property Developer' || userRole === 'Strata Manager') {
      disabled = !firstname || !lastname || !email || !phone || !userRole;
    }

    if (userRole === 'Service Manager') {
      disabled =
        !firstname || !lastname || !email || !phone || !userRole || !propertyId;
    }

    return disabled;
  };

  // Clears a specific error from the errors state.
  const onErrorEmpty = (value) => {
    let newErrors = { ...state.errors };
    newErrors[value] = '';

    setState((prevState) => ({
      ...prevState,
      errors: newErrors,
    }));
  };

  // Fetches address predictions from Google Places API as user types.
  const getLocation = async (value) => {
    setState((prevState) => ({
      ...prevState,
      location: value,
    }));

    const response = await client.getLocations(value);

    if (response.status !== 200) {
      console.error('Cannot find this address');

      return setState((prevState) => ({
        ...prevState,
        predictions: [],
      }));
    }

    const responseJson = await response.json();
    const data = JSON.parse(responseJson);

    setState((prevState) => ({
      ...prevState,
      predictions: data.predictions,
    }));
  };

  // Handles selection of an address from predictions. Todo: Refactor logic in here to be less messy.
  const onClickLocation = async (address) => {
    const apiurl = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&address=${address}`;

    try {
      // Get latitude and longitude for the address.
      const result = await fetch(apiurl);
      const json = await result.json();
      let latitude = json.results[0].geometry.location.lat;
      let longitude = json.results[0].geometry.location.lng;

      // Use reverse geocoding to get detailed address components.
      const apiurl1 = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&latlng=${latitude},${longitude}`;

      const getResult = await fetch(apiurl1);
      const addressObj = await getResult.json();

      // Initialize address component variables.
      let addressLine1;
      let city;
      let state;
      let pincode;
      let addressData = addressObj.results[0].address_components;

      // Parse address components from the API response.
      if (addressData && addressData.length > 0) {
        addressData.forEach((data) => {
          // Extract street number.
          if (data.types.indexOf('street_number') != -1) {
            addressLine1 = data.long_name;
          }

          if (data.types.indexOf('route') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }

          if (data.types.indexOf('neighborhood') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }

          if (data.types.indexOf('sublocality_level_2') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }

          if (data.types.indexOf('sublocality_level_1') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }

          if (data.types.indexOf('locality') != -1) {
            city = data.long_name;
          }

          if (data.types.indexOf('administrative_area_level_1') != -1) {
            state = data.long_name;
          }

          if (data.types.indexOf('postal_code') != -1) {
            pincode = data.long_name;
          }
        });
        onSetContactFormData({
          address: {
            addressLine1: addressLine1,
            city: city,
            province: state,
            postalCode: pincode,
          },
        });

        setState((prevState) => ({
          ...prevState,
          predictions: [],
          location: '',
        }));
      } else {
        console.error('Cannot find this address');
      }
    } catch (err) {
      console.error(
        'Unexpected exception encountered in onClickLocation: ',
        err
      );
    }
  };

  // Formats the contact type display text.
  const contactType = (role) => {
    return role === 'Home Owner' ? variables.homeOwner : role;
  };

  // Determines if the "Send Activation Email" checkbox should be checked.
  const checkBoxValue = () => {
    if (
      (contactFormData.sendActivationEmail &&
        contactFormData.sendActivationEmail === true) ||
      (contactFormData.status && contactFormData.status === 'Invited') ||
      (contactFormData.status && contactFormData.status === 'Active') ||
      (contactFormData.lastLoggedOn && contactFormData.lastLoggedOn !== null)
    ) {
      return true;
    } else {
      return false;
    }
  };

  // Handles selection and removal of properties. Todo: Refactor logic in here to be less messy.
  const selectionForProperties = (e, type) => {
    let list = [],
      assignSuites = [],
      units = '';

    setState((prevState) => ({
      ...prevState,
      propertiesList: e,
    }));

    try {
      if (!!state.suitesList && state.suitesList.length > 0) {
        units = state.suitesList;
      }

      for (var i = 0; i < e.length; i++) {
        if (!!units) {
          let filterUnits = units.filter((x) => x.propertyId == e[i].Id);

          if (filterUnits.length == 1) {
            assignSuites.push(filterUnits[0]);
          } else if (filterUnits.length > 1) {
            assignSuites.push(...filterUnits);
          }
        }
        list.push(e[i].Id);
      }

      if (type == 'remove' && list.length == 0) {
        if (
          contactFormData.userRole == 'Home Owner' ||
          contactFormData.userRole == 'Realtor'
        ) {
          setState((prevState) => ({
            ...prevState,
            propertiesList: [],
            suitesList: [],
          }));
          doClearSuites();
          onSetContactFormData({ properties: [] });
          onSetContactFormData({ Suites: [] });
        }
      }

      let params = {
        Properties: list,
        UserId: user.id,
        Role: user.roles[0].role.name,
      };

      if (list.length > 0) {
        doClearSuites();
        onSetContactFormData({ properties: list });
        setState((prevState) => ({
          ...prevState,
          suitesList: assignSuites,
        }));
        var suitesData = [];

        for (var k = 0; k < list.length; k++) {
          let unitsList = state.suitesList;

          if (!!unitsList) {
            let filterUnits = unitsList.filter((x) => x.propertyId == list[k]);

            if (filterUnits.length == 0) {
              suitesData.push({
                Id: '00000000-0000-0000-0000-000000000000',
                name: '',
                propertyId: list[k],
              });
            }
          }
        }

        if (suitesData.length > 0) {
          suitesData.push(...state.suitesList);
          onSetContactFormData({ Suites: suitesData });
        } else {
          onSetContactFormData({ Suites: state.suitesList });
        }

        onFetchPropertiesSuites(params);
      }
    } catch (e) {
      console.error(
        'Unexpected error encountered in selectionForProperties(): ',
        e
      );
    }
  };

  // Filters and updates the suites list based on selected properties
  const filterList = (e) => {
    let list = [];
    let units = e;
    const properties = state.propertiesList;

    for (let i = 0; i < properties.length; i++) {
      let filterUnits = units.filter((x) => x.propertyId === properties[i].Id);

      if (filterUnits.length > 0) {
        for (var j = 0; j < filterUnits.length; j++) {
          list.push({
            Id: filterUnits[j].Id,
            name: filterUnits[j].name,
            propertyId: properties[i].Id,
          });
        }
      }
    }

    onSetContactFormData({ Suites: list });
    setState((prevState) => ({
      ...prevState,
      suitesList: list,
    }));
  };

  // Determines if the email field should be disabled
  const disableEmailField = () => {
    if (contactFormData.id || contactFormData.isSpark) {
      return true;
    }

    return false;
  };

  return (
    <Form
      headline={(contactFormData.id ? 'Edit' : 'Add a') + ' Contact'}
      onClose={onClose}
      actions={
        <div className="sendResetBtn">
          {loader ? <Loader center={true} /> : null}
          {contactFormData.id ? (
            contactFormData.status !== 'Active' ? (
              <Button
                color="teal"
                size="small"
                label="Send Activation Link"
                disabled={enableButton()}
                onClick={() => sendActivationLinkToUser(contactFormData)}
              />
            ) : (
              <Button
                color="teal"
                size="small"
                label="Send Reset Password Link"
                disabled={enableButton()}
                onClick={() => sendResetPasswordLinkToUser(contactFormData)}
              />
            )
          ) : (
            <Button
              color="semantic"
              size="small"
              label="Save and Add Another"
              disabled={enableButton()}
              onClick={() => validateContactFormData('saveAndAdd')}
              style={{ paddingRight: '3rem' }}
            />
          )}
          <Button
            color="teal"
            size="small"
            label={
              isFromPropertyDeveloper && isFromPropertyDeveloper === true
                ? 'Save Property Developer'
                : 'Save Contact'
            }
            disabled={enableButton()}
            onClick={() => validateContactFormData('saveContact')}
          />
        </div>
      }
      component={
        <ContactFormContent
          contactFormData={contactFormData}
          onSetContactFormData={onSetContactFormData}
          properties={properties}
          propertySuites={propertySuites}
          user={user}
          types={types}
          errors={state.errors}
          onErrorEmpty={onErrorEmpty}
          onFetchPropertySuites={onFetchPropertySuites}
          clearSuites={doClearSuites}
          propertiesList={state.propertiesList}
          suitesList={state.suitesList}
          isFromPropertyDeveloper={isFromPropertyDeveloper}
          getLocation={getLocation}
          predictions={state.predictions}
          onClickLocation={onClickLocation}
          location={state.location}
          selectionForProperties={(e, type) => selectionForProperties(e, type)}
          filterList={(e) => filterList(e)}
          disableEmailField={disableEmailField}
          checkBoxValue={() => checkBoxValue()}
          contactType={(role) => contactType(role)}
        />
      }
    />
  );
};

export default ContactForm;
